import { DownloadOutlined, EditOutlined, SaveOutlined, ProfileOutlined } from "@ant-design/icons";
import { Col, DatePicker, Input, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import { dbDate, dbMonthLg, fileFormatXextension, langDate, langMonth, lgStatus } from "../../../utils/properties";
import { checkRoles, exportFile, showError, showSuccess, trimData } from "../../../utils/utilities";
import AccountingBookPlanning from "../../../services/api/AccountingBookPlanning";
import dayjs from "dayjs";

const Planning = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    tableContent: [],
    modal: {
      open: false,
      type: "",
      data: {},
    },
    loading: false,
  });

  useEffect(() => {
    if (filters.obj?.year) {
      dataList(filters.obj?.year);
    }
  }, [filters]);

  const formikLg = useFormik({
    initialValues: {
      datePlanning:
        get(state.modal.data, "datePlanning", null) !== null ? dayjs(state.modal.data.datePlanning, dbDate) : null,
      fileName: get(state.modal.data, "fileName", null),
      status: get(state.modal.data, "status", null),
      yearMonth: get(state.modal.data, "yearMonth", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.datePlanning) {
        errors.datePlanning = "Required!";
      }
      if (!values.fileName) {
        errors.fileName = "Required!";
      }
      if (!values.status) {
        errors.status = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      const saveData = {
        ...values,
        datePlanning: dayjs(values.datePlanning, dateFormat).format(dbDate),
      };
      const { error } = await AccountingBookPlanning.savePlanning(values.yearMonth, saveData);
      if (!error) {
        showSuccess();
        closeModal();
        dataList(filters.obj?.year);
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    },
  });

  const dataList = async (year) => {
    dispatch(setLoading(true));
    const { data, error } = await AccountingBookPlanning.getPlanning(year);
    if (!error && data) {
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikLg.resetForm();
    setState((prevState) => ({ ...prevState, modal: { open: false, type: "", data: {} } }));
  };

  const editCallback = async (yearMonth, prevMonth = undefined) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { open: true, type: "edit" } }));
    const { data, error } = await AccountingBookPlanning.getPlanningDetail(yearMonth);
    if (!error && data) {
      const modalData = { ...data, prevMonth: prevMonth };
      setState((prevState) => ({ ...prevState, modal: { open: true, type: "edit", data: modalData } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const downloadCallback = async (yearMonth) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const exportResponse = await AccountingBookPlanning.downloadPlanningDetail(yearMonth);
    if (exportResponse) {
      const downloadType = fileFormatXextension["pdf"];
      const blob = new Blob([exportResponse], { type: downloadType });
      if (blob.size > 0) {
        exportFile(blob, `${yearMonth}-report.pdf`);
        dataList(filters.obj?.year);
      } else {
        showError("fileError");
      }
    } else {
      showError("fileError");
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const downloadIndexCallback = async (yearMonth) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const exportResponse = await AccountingBookPlanning.downloadPlanningIndex(yearMonth);
    if (exportResponse) {
      const blob = new Blob([exportResponse]);
      if (blob.size > 0) {
        exportFile(blob, `${yearMonth}-index.csv`);
      } else {
        showError("fileError");
      }
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const columns = [
    {
      title: t("lg.table.yearMonth"),
      dataIndex: "yearMonth",
      key: "yearMonth",
      render: (yearMonth) =>
        yearMonth !== null ? dayjs(yearMonth.toString(), dbMonthLg).format(langMonth[toLower(currentLanguage)]) : "-",
    },
    {
      title: t("lg.table.datePlanning"),
      dataIndex: "datePlanning",
      key: "datePlanning",
      render: (datePlanning) =>
        datePlanning !== null ? dayjs(datePlanning, dbDate).format(langDate[toLower(currentLanguage)]) : "-",
    },
    {
      title: t("lg.table.dateExecution"),
      dataIndex: "dateExecution",
      key: "dateExecution",
      render: (dateExecution) =>
        dateExecution !== null ? dayjs(dateExecution, dbDate).format(langDate[toLower(currentLanguage)]) : "-",
    },
    {
      title: t("lg.table.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => t(`lg.status.${status}`),
    },
    {
      title: t("lg.table.fileName"),
      dataIndex: "fileName",
      key: "fileName",
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => (
        <div className="space-around">
          <Button
            clickCallback={() =>
              editCallback(
                data.yearMonth,
                state.tableContent.find((t) => t.yearMonth === data.yearMonth - 1)
              )
            }
            currentIcon={<EditOutlined className="icon-medium" />}
            isDisabled={data.status === "DEFINITIVE" && !checkRoles(userInfo.roles, ["SYSTEM_IT"])}
            type="transparent"
          />
          <Button
            className={data.status === "EXECUTED" && data.pdfCreatedDate === null ? "notification-error" : ""}
            clickCallback={() => downloadCallback(data.yearMonth)}
            currentIcon={<DownloadOutlined className="icon-medium" />}
            isDisabled={
              data.status === "PLANNED" ||
              data.status === "CREATED" ||
              state.tableContent.find((t) => t.yearMonth === data.yearMonth - 1)?.pdfCreatedDate === null
            }
            type="transparent"
          />
          <Button
            clickCallback={() => downloadIndexCallback(data.yearMonth)}
            currentIcon={<ProfileOutlined className="icon-medium" />}
            isDisabled={data.status !== "DEFINITIVE"}
            type="transparent"
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey={(item) => item.yearMonth}
        dataSource={state.tableContent}
        loading={state.loading}
        columns={columns}
        size="small"
        pagination={false}
      />
      <Modal
        open={state.modal.open}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.yearMonth
            ? dayjs(state.modal.data.yearMonth.toString(), dbMonthLg).format(langMonth[toLower(currentLanguage)])
            : ""
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikLg.submitForm}
            isDisabled={!(formikLg.dirty && formikLg.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]} justify="space-around" className="mb-1em">
          <Col lg={22} md={22} xs={24} sm={22}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="fileName">
                {t("lg.table.fileName")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="fileName"
                name="fileName"
                onChange={formikLg.handleChange}
                onBlur={formikLg.handleBlur}
                value={formikLg.values.fileName}
                maxLength={25}
                disabled={
                  formikLg.values.status === "EXECUTED" ||
                  formikLg.values.status === "TEMPORARY" ||
                  formikLg.values.status === "DEFINITIVE"
                }
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="datePlanning">
                {t("lg.table.datePlanning")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                disabled={formikLg.values.status === "DEFINITIVE"}
                className="w-100"
                allowClear
                id="datePlanning"
                name="datePlanning"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikLg.setFieldValue("status", "PLANNED");
                  return formikLg.setFieldValue("datePlanning", date);
                }}
                onBlur={formikLg.handleBlur}
                value={formikLg.values.datePlanning}
                disabledDate={(current) =>
                  state.modal.data?.prevMonth && dayjs(state.modal.data?.prevMonth?.datePlanning, dbDate) > dayjs()
                    ? current &&
                      (current < dayjs(state.modal.data?.yearMonth, dbMonthLg) ||
                        current < dayjs(state.modal.data?.prevMonth?.datePlanning, dbDate)) // select after date planning of prev month
                    : dayjs(state.modal.data?.yearMonth, dbMonthLg) < dayjs()
                      ? current && current < dayjs().subtract(1, "days")
                      : current && current < dayjs(state.modal.data?.yearMonth, dbMonthLg)
                }
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="status">
                {t("lg.table.status")}*
              </label>
            </Row>
            <Row>
              <Select
                disabled={formikLg.values.status === "PLANNED"}
                allowClear
                onClear={() => {
                  formikLg.setFieldValue("status", null);
                }}
                showSearch
                placeholder={t("lg.table.status")}
                id="status"
                name="status"
                className="w-100"
                onBlur={formikLg.handleBlur("status")}
                onChange={formikLg.handleChange("status")}
                value={formikLg.values.status}
              >
                {lgStatus.map((item, i) => (
                  <Select.Option
                    disabled={
                      (formikLg.values.status === "TEMPORARY" && item === "EXECUTED") ||
                      (state.modal.data?.pdfCreatedDate === null && item === "TEMPORARY") ||
                      (state.modal.data?.pdfCreatedDate === null && item === "DEFINITIVE")
                    }
                    key={i}
                    value={item}
                  >
                    {t(`lg.status.${item}`)}
                  </Select.Option>
                ))}
              </Select>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default Planning;
